/**
 * One conversation of Messages (collab/Messages.jsx): the bubbles (mine on the right, newest at the bottom, by day, the
 * writer's name on the first of a run, the time on each), a "New" line above what was unread, the file a message is
 * about as a chip that opens it, Edit and Delete on your own, and the input at the bottom (Enter sends, Shift+Enter a
 * new line) with each send's state: sending, sent, or not sent with Retry.
 *
 * ChatPane({ projectId, conversation, me, agents, since, canSend, cannotSend?, place?, onOpenFile?, onSent(note),
 *   onChanged(), autoFocus?, agentId?, onAgentChange? })
 * - conversation: one of messagesModel.conversationsOf(); `since`: its read mark when it was opened (the "New" line).
 * - canSend false: `cannotSend` says why instead of the input.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addNote, deleteMessage, deleteNote, editMessage, editNote, sendMessage } from '../../services/CollabService';
import NoteChange from './NoteChange';
import {
  AGENTS, EVERYONE, MESSAGE_MAX, agentRecipientText, chatDays, firstNameOf, isUnread, mentionParts, placeLabel, senderLabel, timeLabel,
} from '../../utils/messagesModel';
import './Collab.css';

const TEXT_FIELD = [{ key: 'text', label: 'Message' }];
// The input grows with what is typed (up to 160px, then it scrolls); where CSS field-sizing exists it does that itself.
const grow = (el) => {
  if (!el?.style) { return; }
  el.style.height = 'auto';
  if (el.scrollHeight) { el.style.height = `${Math.min(el.scrollHeight + 2, 160)}px`; }
};
let sequence = 0;

/** What the conversation is, under its name. */
function aboutLine(conversation) {
  if (conversation.key === EVERYONE) { return 'Everyone in this project. Type @ and a first name to notify someone.'; }
  if (conversation.key === AGENTS) { return 'Notes for the AI agents working here: they get them with their next action.'; }
  return `Only you and ${firstNameOf(conversation.label)}. ${firstNameOf(conversation.label)} gets a notification.`;
}

function Bubble({ item, me, agents, projectId, isNew, sent, onOpenFile, onChanged, chat }) {
  const { note, first, mine } = item;
  const place = note.path ? { path: note.path, line: note.line } : null;
  const to = chat ? null : agentRecipientText(note, agents);
  // A message of the chat (a viewer may change their own); a note for the agents, or an older Everyone pair, is a note.
  const change = chat && note.ids.length === 1 ? { edit: editMessage, remove: deleteMessage } : { edit: editNote, remove: deleteNote };
  return (
    <li className={`collab-chat-msg${mine ? ' is-mine' : ''}${first ? ' is-first' : ''}${isNew ? ' is-unread' : ''}`} data-testid="message-row">
      {first && (
        <div className="collab-chat-author">
          {mine ? 'You' : senderLabel(note, me)}{to ? <span className="collab-muted"> {to}</span> : null}
        </div>
      )}
      <div className="collab-chat-bubble">
        {place && (onOpenFile
          ? <button className="collab-chat-chip" onClick={() => onOpenFile(place)} title="Open the file in Code" type="button">{placeLabel(place)}</button>
          : <span className="collab-chat-chip">{placeLabel(place)}</span>)}
        <p className="collab-message-text">
          {mentionParts(note.text).map((part, index) => (part.mention ? <span className="collab-mention" key={index}>{part.text}</span> : <React.Fragment key={index}>{part.text}</React.Fragment>))}
        </p>
        <span className="collab-chat-meta">
          <time dateTime={note.at}>{timeLabel(note.at)}</time>{note.editedAt ? ' · edited' : ''}{sent ? ' · Sent' : ''}
        </span>
      </div>
      {note.canChange && (
        <NoteChange
          fields={TEXT_FIELD}
          noun="message"
          onChanged={onChanged}
          onDelete={() => Promise.all(note.ids.map((id) => change.remove(projectId, id)))}
          onSave={(values) => Promise.all(note.ids.map((id) => change.edit(projectId, id, values.text)))}
          values={{ text: note.text }}
        />
      )}
    </li>
  );
}

Bubble.propTypes = {
  agents: PropTypes.array,
  chat: PropTypes.bool,
  isNew: PropTypes.bool,
  item: PropTypes.object.isRequired,
  me: PropTypes.object,
  onChanged: PropTypes.func,
  onOpenFile: PropTypes.func,
  projectId: PropTypes.string,
  sent: PropTypes.bool,
};

export default function ChatPane({
  projectId, conversation, me = null, agents = [], since = null, canSend, cannotSend = null, place = null, onOpenFile = null, onSent, onChanged,
  autoFocus = false, agentId = null, onAgentChange = null,
}) {
  const [drafts, setDrafts] = useState({});
  const [outbox, setOutbox] = useState([]);
  const [sentId, setSentId] = useState(null);
  const [about, setAbout] = useState(Boolean(place?.path));
  const logRef = useRef(null);
  const inputRef = useRef(null);
  const atBottom = useRef(true);
  const { key } = conversation;
  const text = drafts[key] || '';
  const days = chatDays(conversation.messages, me);
  const pending = outbox.filter((item) => item.key === key);
  const firstNew = conversation.messages.find((note) => since !== undefined && isUnread(note, me, since));

  useEffect(() => { setAbout(Boolean(place?.path)); }, [place?.path, place?.line]);
  useEffect(() => { if (autoFocus) { inputRef.current?.focus?.(); } }, [autoFocus, key]);
  // Newest at the bottom: kept there when you were there, sent something, or opened another conversation.
  const lastId = conversation.last?.id || '';
  useLayoutEffect(() => { atBottom.current = true; }, [key]);
  useLayoutEffect(() => {
    const log = logRef.current;
    if (log && atBottom.current) { log.scrollTop = log.scrollHeight; }
  }, [key, lastId, pending.length]);

  const deliver = async(item) => {
    setOutbox((list) => [...list.filter((x) => x.id !== item.id), { ...item, status: 'pending', error: null }]);
    const where = item.place?.path ? { path: item.place.path, line: item.place.line } : {};
    try {
      const out = item.key === AGENTS
        ? await addNote(projectId, { to: 'agents', ...(item.agentId ? { agent: item.agentId } : {}), text: item.text, ...where })
        : await sendMessage(projectId, { to: item.key === EVERYONE ? 'people' : item.email, text: item.text, ...where });
      setOutbox((list) => list.filter((x) => x.id !== item.id));
      if (out?.note?.id) {
        setSentId(out.note.id);
        onSent({ ...out.note, canChange: true });
      }
      if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent('collab:note', { detail: { projectId, path: where.path } })); }
    } catch (err) {
      setOutbox((list) => list.map((x) => (x.id === item.id ? { ...x, status: 'failed', error: err?.message || 'Not sent.' } : x)));
    }
  };

  const send = (event) => {
    event?.preventDefault?.();
    const body = text.trim();
    if (!body || !canSend || body.length > MESSAGE_MAX) { return; }
    sequence += 1;
    atBottom.current = true;
    setDrafts((all) => ({ ...all, [key]: '' }));
    if (inputRef.current?.style) { inputRef.current.style.height = ''; }
    deliver({ id: `out-${sequence}`, key, email: conversation.email, agentId, text: body, place: about && place?.path ? place : null, at: new Date().toISOString() });
  };

  const label = key === EVERYONE ? 'Message everyone' : key === AGENTS ? 'Note for the AI agents' : `Message ${conversation.label}`;
  const chat = key !== AGENTS;
  return (
    <section aria-label={conversation.key === EVERYONE ? 'Everyone' : conversation.label} className="collab-chat-main" data-testid="chat-pane">
      <header className="collab-chat-head">
        <strong>{conversation.label}</strong>
        <span className="collab-muted collab-small">{aboutLine(conversation)}</span>
      </header>
      <ol
        aria-label={`${label.replace(/^Message /, 'Messages with ').replace('Note for the AI agents', 'Notes for the AI agents')}, newest last`}
        className="collab-chat-log"
        onScroll={(event) => { const el = event.currentTarget; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}
        ref={logRef}
      >
        {days.length === 0 && pending.length === 0 && (
          <li className="collab-messages-empty" data-testid="messages-empty">
            <strong>No messages yet.</strong>
            <span>{key === EVERYONE ? 'Say hello to everyone in this project.' : key === AGENTS ? 'Notes between people and the AI agents show here.' : `Write to ${firstNameOf(conversation.label)}: only the two of you see it here.`}</span>
          </li>
        )}
        {days.map((day) => (
          <React.Fragment key={day.day}>
            <li className="collab-chat-day" role="presentation"><span>{day.label}</span></li>
            {day.items.map((item) => (
              <React.Fragment key={item.note.id}>
                {firstNew && firstNew.id === item.note.id && <li className="collab-chat-new" data-testid="messages-new-line" role="presentation"><span>New</span></li>}
                <Bubble
                  agents={agents}
                  chat={chat}
                  isNew={Boolean(firstNew) && isUnread(item.note, me, since)}
                  item={item}
                  me={me}
                  onChanged={onChanged}
                  onOpenFile={onOpenFile}
                  projectId={projectId}
                  sent={item.note.id === sentId && item.note.id === lastId}
                />
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
        {pending.map((item) => (
          <li className={`collab-chat-msg is-mine is-${item.status}`} data-testid="message-pending" key={item.id}>
            <div className="collab-chat-bubble">
              {item.place && <span className="collab-chat-chip">{placeLabel(item.place)}</span>}
              <p className="collab-message-text">{item.text}</p>
              <span className="collab-chat-meta">{item.status === 'failed' ? 'Not sent' : 'Sending…'}</span>
            </div>
            {item.status === 'failed' && (
              <div className="collab-small" role="alert">
                <span className="collab-error">{item.error}</span>{' '}
                <button className="collab-link" onClick={() => deliver(item)} type="button">Retry</button>{' · '}
                <button className="collab-link" onClick={() => setOutbox((list) => list.filter((x) => x.id !== item.id))} type="button">Discard</button>
              </div>
            )}
          </li>
        ))}
      </ol>
      {canSend ? (
        <form aria-label={label} className="collab-chat-compose" data-testid="message-composer" onSubmit={send}>
          {key === AGENTS && onAgentChange && (
            <label className="collab-chat-to">
              <span>To</span>
              <select data-testid="message-to" onChange={(event) => onAgentChange(event.target.value || null)} value={agentId || ''}>
                <option value="">All AI agents</option>
                {agents.map((a) => <option key={a.agentId} value={a.agentId}>{a.online ? '● ' : ''}{a.label}{a.online ? '' : ' (not active now)'}</option>)}
              </select>
            </label>
          )}
          {place?.path && about && (
            <span className="collab-chat-chip is-removable" data-testid="message-about">
              About {placeLabel(place)}
              <button aria-label={`Not about ${placeLabel(place)}`} onClick={() => setAbout(false)} type="button">×</button>
            </span>
          )}
          <div className="collab-chat-input">
            <textarea
              aria-label={label}
              data-testid="message-text"
              maxLength={MESSAGE_MAX}
              onChange={(event) => { grow(event.target); setDrafts((all) => ({ ...all, [key]: event.target.value })); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) { send(event); }
              }}
              placeholder={key === EVERYONE ? 'Message everyone (@name to notify)' : key === AGENTS ? 'e.g. I am editing the login form, leave it to me.' : `Message ${firstNameOf(conversation.label)}`}
              ref={inputRef}
              rows={1}
              value={text}
            />
            <button className="collab-btn is-primary" data-testid="message-send" disabled={!text.trim()} type="submit">Send</button>
          </div>
          <span className="collab-muted collab-small collab-chat-hint">
            Enter sends, Shift+Enter starts a new line.{text.length > MESSAGE_MAX - 200 ? ` ${text.length}/${MESSAGE_MAX}` : ''}
          </span>
        </form>
      ) : (
        cannotSend && <p className="collab-messages-note" data-testid="messages-viewer">{cannotSend}</p>
      )}
    </section>
  );
}

ChatPane.propTypes = {
  agentId: PropTypes.string,
  agents: PropTypes.array,
  autoFocus: PropTypes.bool,
  canSend: PropTypes.bool,
  cannotSend: PropTypes.string,
  conversation: PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, email: PropTypes.string, messages: PropTypes.array, last: PropTypes.object }).isRequired,
  me: PropTypes.object,
  onAgentChange: PropTypes.func,
  onChanged: PropTypes.func,
  onOpenFile: PropTypes.func,
  onSent: PropTypes.func.isRequired,
  place: PropTypes.shape({ path: PropTypes.string, line: PropTypes.number }),
  projectId: PropTypes.string.isRequired,
  since: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};
